/**
 * MessagingWorkspace Component
 * Main workspace for messaging with channel list, message thread, and DM management
 *
 * Features:
 * - Channel list with unread counts
 * - Message thread with priority and action menus
 * - Tabs: Members (member conversations) · Team (staff channels and DMs) · Mailbox Email
 * - DM Management (filtering, reassignment, bulk operations) lives in Organization
 *   Settings → Member Inbox (TRUS-745); the 'dm-management' view still renders
 *   for pop-out tabs saved before the move.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PUSH_OPEN_CHANNEL_EVENT, takePendingOpenChannel } from '@/lib/pushNavigation';
import { setActiveConversation } from '@/lib/desktopMessageNotifications';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Settings,
  Users,
  Search,
  X,
  Hash,
  Lock,
  Volume2,
  MessageSquare,
  Mail,
  Filter,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  messagingApi,
  Channel,
  ChannelDetail,
  ChannelType,
  Message,
  CreateChannelRequest,
} from '@/services/messagingApi';
import { userManagementAPI, OrganizationUser } from '@/services/api/userManagement';
import { membersAPI } from '@/services/api/members';
import { ChannelList } from './ChannelList';
import { MessageThread } from './MessageThread';
import { ThreadPanel } from './ThreadPanel';
import { DMChannelManagement } from './DMChannelManagement';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { cn } from '@/lib/utils';
import EmailInboxView from '@/components/inbox/EmailInboxView';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { OpenInNewMenu } from './popout/OpenInNewMenu';
import type { MessagingTabSpec } from '@/lib/popout';
import { MessageNavigationProvider } from './linkify';
import { toast } from '../ui/use-toast';
import { useAppTranslation } from '@/contexts/TranslationContext';
import type { CallMode } from '@/services/callsApi';
import { callStore } from './calls/callStore';
import { OPEN_CALL_CHANNEL_EVENT } from './calls/callStore';
// TRUS-691: ?meeting= deep links + the sidebar's upcoming meetings.
import { MeetingsHost } from './meetings/MeetingsHost';
import { UpcomingMeetingsSection } from './meetings/UpcomingMeetings';
import { getCurrentUserId } from '@/utils/auth';
// TRUS-709: the member Inbox (one row per member conversation) and deep links into it.
import { MemberInboxList, channelFromConversation } from './inbox/MemberInboxList';
import { useMessagingSettings } from './inbox/MemberInboxSettingsPanel';
import { useIsOrgAdmin } from './channelActions';
import { OPEN_MEMBER_INBOX_EVENT, takePendingMemberInbox } from '@/lib/memberInboxNavigation';
import { config as productConfig } from '@/src/config';

export type WorkspaceView = 'members' | 'channels' | 'dm-management' | 'email';

/** Where the workspace opens by default: the member Inbox (Triveny has no members: channels). */
export const DEFAULT_WORKSPACE_VIEW: WorkspaceView = productConfig.isTriveny ? 'channels' : 'members';

interface MessagingWorkspaceProps {
  defaultChannelId?: number;
  headerActions?: React.ReactNode;
  /** When set (e.g. from a CSR handoff "open"), auto-open that member's DM channel. */
  initialMemberId?: number | null;
  /** View to start on (messaging pop-out tabs open straight onto DMs or Mailbox). */
  initialView?: WorkspaceView;
  /** Told which channel is open, so a pop-out tab can show its name. */
  onSelectedChannelChange?: (channel: { id: number; name: string } | null) => void;
  /** A call pop-out (TRUS-668): join this call full-window on open. Needs defaultChannelId. */
  initialCallId?: number;
  initialCallMode?: CallMode;
  /**
   * v3: the popped-out call belongs to a conversation this user cannot read
   * (an invite): join the call, never open the channel.
   */
  initialCallStandalone?: boolean;
}

/** Automation / CI logins that nobody should be able to message. */
const AUTOMATION_EMAIL_DOMAINS = ['@anthropic.com'];

/**
 * Staff you can start a DM / channel with or add to one: the org's staff
 * accounts, without portal members (TRUS-434).
 *
 * Deliberately NOT `excludeInternal`: that filter (meant for case-manager style
 * dropdowns) also drops platform admins (super_admin / ops_admin) and every
 * @trust.care / @weavein.ai account, which made it impossible to message a
 * platform admin from the Inbox. Only automation accounts stay hidden.
 */
export async function listMessagingStaff(cboId: string | null): Promise<OrganizationUser[]> {
  if (!cboId) return [];
  const users = await userManagementAPI.listUsers(parseInt(cboId, 10), { staffOnly: true });
  return users.filter((u) => {
    const email = (u.email || '').toLowerCase();
    return !AUTOMATION_EMAIL_DOMAINS.some((d) => email.endsWith(d));
  });
}

export function MessagingWorkspace({
  defaultChannelId,
  headerActions,
  initialMemberId,
  initialView = DEFAULT_WORKSPACE_VIEW,
  onSelectedChannelChange,
  initialCallId,
  initialCallMode,
  initialCallStandalone = false,
}: MessagingWorkspaceProps) {
  const { t } = useAppTranslation();
  const [activeView, setActiveView] = useState<WorkspaceView>(initialView);
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showCreateDM, setShowCreateDM] = useState(false);
  const [showAddPeople, setShowAddPeople] = useState(false);
  const [showThreadPanel, setShowThreadPanel] = useState(false);
  const [activeThread, setActiveThread] = useState<Message | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [dmPreviewChannel, setDmPreviewChannel] = useState<Channel | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  // Resizable + collapsible left sidebar (channel/DM list).
  const [sidebarWidth, setSidebarWidth] = useState(288);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = sidebarWidth;
    const onMove = (ev: MouseEvent) => {
      setSidebarWidth(Math.min(560, Math.max(200, startW + (ev.clientX - startX))));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  // Debug removed — v2 DM fix confirmed deployed
  const queryClient = useQueryClient();
  const isOrgAdmin = useIsOrgAdmin();
  // Whether the member Inbox endpoint answered; while it does, member DMs live
  // there and the Channels list keeps to channels and staff DMs.
  const [inboxSource, setInboxSource] = useState<'inbox' | 'fallback' | null>(null);
  // Org messaging settings (TRUS-709): caseload mode adds the admins' Unrouted lane.
  const { data: messagingSettings } = useMessagingSettings();
  const caseloadMode = messagingSettings?.inbox_member_visibility === 'caseload';
  const isListView = activeView === 'members' || activeView === 'channels';

  // Fetch all channels to find one by ID when selecting from DM Management
  const { data: allChannels } = useQuery({
    queryKey: ['messaging-channels'],
    queryFn: () => messagingApi.listChannels(),
  });

  // Fetch channel details when selected (channels view)
  const { data: channelDetail } = useQuery({
    queryKey: ['channel-detail', selectedChannel?.id],
    queryFn: () =>
      selectedChannel ? messagingApi.getChannel(selectedChannel.id) : null,
    enabled: !!selectedChannel,
  });

  // Fetch channel details for DM Management preview panel
  const { data: dmPreviewDetail } = useQuery({
    queryKey: ['channel-detail', dmPreviewChannel?.id],
    queryFn: () =>
      dmPreviewChannel ? messagingApi.getChannel(dmPreviewChannel.id) : null,
    enabled: !!dmPreviewChannel,
  });

  const handleSelectChannel = (channel: Channel) => {
    setSelectedChannel(channel);
    setShowThreadPanel(false);
    setActiveThread(null);
  };

  // Handle selecting a channel from DM Management by ID
  // Keep the user on the DM Management view — show conversation in a slide-over panel.
  // Member DM channels may not be in `allChannels` (which is paginated + historically
  // membership-scoped), so fetch by id directly as a fallback instead of silently no-op'ing.
  // Open a channel's full conversation reliably (same path as the Channels list),
  // regardless of viewport. The old behavior set a `dmPreviewChannel` slide-over that
  // was `hidden` below the lg breakpoint and swallowed fetch errors, so DM-management
  // clicks "did nothing" on smaller screens / for channels not in the loaded page.
  const openChannelById = async (channelId: number) => {
    setOpenError(null);
    let ch = allChannels?.find(c => c.id === channelId) || null;
    if (!ch) {
      try {
        ch = (await messagingApi.getChannel(channelId)) as unknown as Channel;
      } catch (e) {
        console.error('Failed to open channel', channelId, e);
        setOpenError('Could not open this conversation — it may have moved or you may not have access.');
        return;
      }
    }
    if (ch) {
      setSelectedChannel(ch);
      // A member conversation opens in the member Inbox, anything else in Channels.
      setActiveView(ch.dm_member_id != null && !productConfig.isTriveny ? 'members' : 'channels');
      setDmPreviewChannel(null);
    }
  };
  const handleSelectChannelById = openChannelById;

  // A `#channel` link in a message. Opens it like a pick from the channel list;
  // when the viewer cannot load it (not a member of a private channel, deleted),
  // the backend 404s and they get a friendly toast instead of an error strip.
  const openChannelFromLink = async (channelId: number, name: string) => {
    let ch = allChannels?.find((c) => c.id === channelId) || null;
    if (!ch) {
      try {
        ch = (await messagingApi.getChannel(channelId)) as unknown as Channel;
      } catch {
        ch = null;
      }
    }
    if (!ch) {
      toast({
        title: t('messaging.mentions.channelUnavailableTitle', 'Channel unavailable'),
        description: t(
          'messaging.mentions.channelUnavailable',
          'You do not have access to #{{name}}, or it no longer exists.',
          { name },
        ),
      });
      return;
    }
    setDmPreviewChannel(null);
    setActiveView('channels');
    handleSelectChannel(ch);
  };

  // Deep-link: open a member's conversation (a CSR handoff "open", a workflow
  // step's "Open in Comms Hub", an old 2-Way Inbox link). The channel list is
  // capped, so a member not in it is resolved with the get-or-create DM call.
  const openMemberConversation = useCallback(
    async (memberId: number) => {
      setOpenError(null);
      let ch: Channel | null = allChannels?.find((c) => c.dm_member_id === memberId) || null;
      if (!ch) {
        try {
          ch = await messagingApi.createDM(undefined, memberId);
        } catch (e) {
          console.error('Failed to open member conversation', memberId, e);
          setOpenError(
            t(
              'messaging.inbox.openMemberFailed',
              'Could not open this member’s conversation — you may not have access to this member.',
            ),
          );
          return;
        }
      }
      setSelectedChannel(ch);
      setDmPreviewChannel(null);
      setShowThreadPanel(false);
      setActiveThread(null);
      setActiveView('members');
    },
    [allChannels, t],
  );
  const openedMemberRef = useRef<number | null>(null);
  useEffect(() => {
    if (initialMemberId == null || openedMemberRef.current === initialMemberId) return;
    // Wait for the channel list (avoids a needless create call) unless it failed.
    if (!allChannels) return;
    openedMemberRef.current = initialMemberId;
    void openMemberConversation(initialMemberId);
  }, [initialMemberId, allChannels, openMemberConversation]);
  useEffect(() => {
    const parked = takePendingMemberInbox();
    if (parked != null) void openMemberConversation(parked);
    const onOpen = () => {
      const id = takePendingMemberInbox();
      if (id != null) void openMemberConversation(id);
    };
    window.addEventListener(OPEN_MEMBER_INBOX_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_MEMBER_INBOX_EVENT, onOpen);
    // Parked requests are taken once, on mount; later ones arrive as events.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link: open a specific channel (a pop-out "channel:<id>" tab). Once only,
  // so returning to the list is not undone by the next channels refetch.
  const [defaultChannelOpened, setDefaultChannelOpened] = useState(false);
  useEffect(() => {
    if (defaultChannelId == null || defaultChannelOpened || initialCallStandalone) return;
    setDefaultChannelOpened(true);
    void openChannelById(defaultChannelId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultChannelId, defaultChannelOpened]);

  // TRUS-668: a call pop-out takes the call over from the window it came from.
  const initialCallOpened = useRef(false);
  useEffect(() => {
    if (initialCallOpened.current || initialCallId == null || defaultChannelId == null) return;
    initialCallOpened.current = true;
    callStore.open({
      callId: initialCallId,
      channelId: defaultChannelId,
      mode: initialCallMode === 'audio' ? 'audio' : 'video',
      fullWindow: true,
      standalone: initialCallStandalone,
    });
  }, [initialCallId, initialCallMode, defaultChannelId, initialCallStandalone]);

  // Joining a call from the incoming banner (or a ?call= link) shows its
  // conversation — also when the link was opened before this pane mounted.
  useEffect(() => {
    const pending = callStore.takePendingChannel();
    if (pending != null) void openChannelById(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const onOpen = (e: Event) => {
      callStore.takePendingChannel();
      const id = Number((e as CustomEvent).detail?.channelId);
      if (Number.isFinite(id) && id > 0) void openChannelById(id);
    };
    window.addEventListener(OPEN_CALL_CHANNEL_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CALL_CHANNEL_EVENT, onOpen);
  });

  // A clicked push notification (TRUS-668): open its channel, whether this
  // workspace was already on screen or is mounting because of the click.
  useEffect(() => {
    const parked = takePendingOpenChannel();
    if (parked) void openChannelById(parked);
    const onOpen = () => {
      const id = takePendingOpenChannel();
      if (id) void openChannelById(id);
    };
    window.addEventListener(PUSH_OPEN_CHANNEL_EVENT, onOpen);
    return () => window.removeEventListener(PUSH_OPEN_CHANNEL_EVENT, onOpen);
  });

  // The call panel docks inside this pane while it is on screen.
  const registerCallDock = useCallback((el: HTMLDivElement | null) => callStore.setDock(el), []);

  // The conversation on screen: no message notification for it while this
  // window is focused (lib/desktopMessageNotifications).
  const onScreenChannelId = isListView ? selectedChannel?.id ?? null : null;
  useEffect(() => {
    setActiveConversation(onScreenChannelId);
    return () => setActiveConversation(null);
  }, [onScreenChannelId]);

  useEffect(() => {
    if (!onSelectedChannelChange) return;
    onSelectedChannelChange(
      selectedChannel
        ? { id: selectedChannel.id, name: selectedChannel.display_name || selectedChannel.name || '' }
        : null,
    );
  }, [selectedChannel, onSelectedChannelChange]);

  // What "open in new tab / window" opens: the conversation on screen, else this view.
  const popoutTabs = (): { tabs: MessagingTabSpec[] } => {
    if (isListView && selectedChannel) {
      return { tabs: [{ kind: 'channel', channelId: selectedChannel.id }] };
    }
    if (activeView === 'members') return { tabs: [{ kind: 'members' }] };
    if (activeView === 'dm-management') return { tabs: [{ kind: 'dms' }] };
    if (activeView === 'email') return { tabs: [{ kind: 'mailbox' }] };
    return { tabs: [{ kind: 'channels' }] };
  };

  const handleOpenThread = (message: Message) => {
    setActiveThread(message);
    setShowThreadPanel(true);
  };

  const handleCloseThread = () => {
    setShowThreadPanel(false);
    setActiveThread(null);
  };

  return (
    <MessageNavigationProvider value={{ openChannel: openChannelFromLink }}>
    <div className="relative flex flex-col h-full max-h-full bg-white dark:bg-slate-950 overflow-hidden">
      <div ref={registerCallDock} className="pointer-events-none absolute inset-0 z-40" data-testid="call-dock" />
      <MeetingsHost currentUserId={getCurrentUserId()} />
      {openError && (
        <div className="flex-shrink-0 flex items-center justify-between gap-2 bg-red-50 px-4 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
          <span>{openError}</span>
          <button onClick={() => setOpenError(null)} className="text-red-500 hover:text-red-700" aria-label="Dismiss">✕</button>
        </div>
      )}
      {/* View Toggle Header — always visible, never scrolls */}
      <div className="flex-shrink-0 z-30 flex items-center justify-between gap-2 px-3 py-2 border-b bg-slate-50 dark:bg-slate-900 sm:px-4">
        <div className="flex min-w-0 items-center gap-1 sm:gap-2">
          {/* No separate "Back" control: the Channels tab returns to the channel
              list (on a phone, where the list is hidden behind an open
              conversation, it is the way back). */}
          {!productConfig.isTriveny && (
            <Button
              variant={activeView === 'members' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => { setActiveView('members'); setSelectedChannel(null); setShowThreadPanel(false); setActiveThread(null); }}
              className="gap-1.5 px-2 sm:gap-2 sm:px-3"
              data-testid="workspace-view-members"
            >
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">{t('messaging.inbox.tab', 'Members')}</span>
            </Button>
          )}
          <Button
            variant={activeView === 'channels' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => { setActiveView('channels'); setSelectedChannel(null); setShowThreadPanel(false); setActiveThread(null); }}
            className="gap-1.5 px-2 sm:gap-2 sm:px-3"
            title={t('messaging.workspace.teamHint', 'Channels and direct messages with your team')}
            aria-label={t('messaging.workspace.tabTeam', 'Team')}
            data-testid="workspace-view-team"
          >
            <MessageSquare className="h-4 w-4" />
            {/* TRUS-745: staff channels AND staff DMs — "Channels" read as if DMs lived elsewhere. */}
            <span className="hidden sm:inline">{t('messaging.workspace.tabTeam', 'Team')}</span>
          </Button>
          {/* Mail synced from the user's own Outlook mailbox. This workspace is the
              default "Member Inbox" in the V2 comms hub and the surface case managers
              actually work in, so the email inbox has to be reachable from here — not
              only from the page the hub itself labels "2-Way Inbox (legacy)". */}
          <Button
            variant={activeView === 'email' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => { setActiveView('email'); setSelectedChannel(null); setDmPreviewChannel(null); }}
            className="gap-1.5 px-2 sm:gap-2 sm:px-3"
          >
            <Mail className="h-4 w-4" />
            <span className="hidden sm:inline">{t('messaging.workspace.tabMailbox', 'Mailbox Email')}</span>
            <span className="sm:hidden">{t('messaging.workspace.tabMailboxShort', 'Email')}</span>
          </Button>
        </div>
        <div className="flex min-w-0 items-center gap-3">
          <div className="hidden text-sm text-muted-foreground truncate md:block">
            {activeView === 'dm-management' && 'Filter, reassign, and manage all member DM channels'}
            {activeView === 'channels' && !selectedChannel && t('messaging.workspace.teamHint', 'Channels and direct messages with your team')}
            {activeView === 'email' && 'Email synced from your own Outlook mailbox — assign it to members or undo an assignment'}
            {activeView === 'members' && !selectedChannel && t('messaging.inbox.headerHint', 'Every member conversation, with who owns it and who replied')}
            {selectedChannel && isListView && `Viewing: ${selectedChannel.display_name || selectedChannel.name || 'Channel'}`}
          </div>
          <OpenInNewMenu getTabs={popoutTabs} className="flex-shrink-0" />
          {headerActions && (
            <div className="flex-shrink-0">{headerActions}</div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      {activeView === 'email' ? (
        <div className="flex-1 min-h-0 overflow-hidden bg-white dark:bg-gray-800">
          <EmailInboxView showPopoutControl={false} />
        </div>
      ) : activeView === 'dm-management' ? (
        <div className="flex flex-1 overflow-hidden">
          {/* DM list — hidden on phone when a preview is open, so the preview gets the whole screen */}
          <div
            className={cn(
              'flex-1 overflow-hidden',
              dmPreviewChannel ? 'hidden lg:block lg:border-r' : '',
            )}
          >
            <DMChannelManagement onSelectChannel={handleSelectChannelById} />
          </div>
          {/* Slide-over conversation preview panel — full-width on phone, 480px on lg+ */}
          {dmPreviewChannel && (
            <div className="flex min-h-0 w-full flex-shrink-0 flex-col bg-white dark:bg-slate-950 lg:w-[480px]">
              <div className="px-4 py-3 border-b flex items-center justify-between bg-slate-50 dark:bg-slate-900">
                <div className="min-w-0 flex-1">
                  <h3 className="truncate font-semibold text-sm">
                    {dmPreviewChannel.display_name || dmPreviewChannel.name}
                  </h3>
                  <p className="text-xs text-muted-foreground">Conversation preview</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setDmPreviewChannel(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="min-h-0 flex-1 overflow-hidden">
                <MessageThread
                  channel={dmPreviewDetail || dmPreviewChannel}
                  onOpenThread={handleOpenThread}
                  onChannelRemoved={() => setDmPreviewChannel(null)}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-1 overflow-hidden">
          {/* Channel Sidebar — collapsible + resizable. Full-width on phone when no
              channel selected; hidden once a channel is selected on phone. */}
          {sidebarCollapsed ? (
            <div className={cn('hidden md:flex flex-col items-center border-r flex-shrink-0 w-9 pt-2', selectedChannel ? '' : '')}>
              <button
                onClick={() => setSidebarCollapsed(false)}
                className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                title="Expand sidebar"
              >
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          ) : (
            <div
              className={cn(
                'relative flex flex-col border-r flex-shrink-0',
                selectedChannel ? 'hidden md:flex' : 'flex max-md:!w-full',
              )}
              style={{ width: sidebarWidth }}
            >
              <div className="flex items-center justify-end px-1 pt-1">
                <button
                  onClick={() => setSidebarCollapsed(true)}
                  className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                  title="Collapse sidebar"
                >
                  <ChevronLeft className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                {activeView === 'members' ? (
                  <MemberInboxList
                    selectedChannelId={selectedChannel?.id}
                    onSelect={(row) => handleSelectChannel(channelFromConversation(row))}
                    isAdmin={isOrgAdmin}
                    caseloadMode={caseloadMode}
                    onCreateDM={() => setShowCreateDM(true)}
                    onSourceChange={setInboxSource}
                  />
                ) : (
                  <>
                    <ChannelList
                      selectedChannelId={selectedChannel?.id}
                      onSelectChannel={handleSelectChannel}
                      onCreateChannel={() => setShowCreateChannel(true)}
                      onCreateDM={() => setShowCreateDM(true)}
                      excludeMemberDms={inboxSource === 'inbox'}
                      onChannelRemoved={(id) => {
                        if (selectedChannel?.id === id) { setSelectedChannel(null); setShowThreadPanel(false); setActiveThread(null); }
                      }}
                    />
                    <UpcomingMeetingsSection />
                  </>
                )}
              </div>
              {/* Drag handle (md+ only) */}
              <div
                onMouseDown={startResize}
                className="hidden md:block absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/30 active:bg-primary/50"
                title="Drag to resize"
              />
            </div>
          )}

          {/* Main Content — hidden on phone until a channel is selected, so the channel list isn't squeezed */}
          <div
            className={cn(
              'flex-1 overflow-hidden',
              selectedChannel ? 'flex' : 'hidden md:flex',
            )}
          >
            {selectedChannel ? (
              <>
                {/* Message Thread */}
                <div
                  className={cn(
                    'flex-1 flex flex-col min-w-0 min-h-0',
                    showThreadPanel && 'hidden lg:flex lg:border-r',
                  )}
                >
                  <MessageThread
                    channel={channelDetail || selectedChannel}
                    onOpenThread={handleOpenThread}
                    onAddPeople={() => setShowAddPeople(true)}
                    onChannelRemoved={() => { setSelectedChannel(null); setShowThreadPanel(false); setActiveThread(null); }}
                  />
                </div>

                {/* Thread Panel — overlays as bottom-sheet on phone, fixed 384px on lg+ */}
                {showThreadPanel && activeThread && (
                  <div className="flex min-h-0 w-full flex-col bg-white dark:bg-slate-950 lg:w-96">
                    <div className="px-4 py-3 border-b flex items-center justify-between">
                      <h3 className="font-semibold">Thread</h3>
                      <Button variant="ghost" size="sm" onClick={handleCloseThread}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <ThreadPanel
                      message={activeThread}
                      channelId={selectedChannel.id}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Hash className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <h2 className="text-xl font-semibold mb-2">
                    {activeView === 'members'
                      ? t('messaging.inbox.emptyTitle', 'Member Inbox')
                      : 'Welcome to Messaging'}
                  </h2>
                  <p className="text-sm">
                    {activeView === 'members'
                      ? t('messaging.inbox.emptyHint', 'Select a member conversation to read and reply')
                      : 'Select a channel to start messaging'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Channel Modal */}
      {showCreateChannel && (
        <CreateChannelModal
          onClose={() => setShowCreateChannel(false)}
          onCreated={(channel) => {
            setShowCreateChannel(false);
            setSelectedChannel(channel);
            queryClient.invalidateQueries({ queryKey: ['messaging-channels'] });
          }}
        />
      )}

      {/* Create DM Modal */}
      {showCreateDM && (
        <CreateDMModal
          onClose={() => setShowCreateDM(false)}
          onCreated={(channel) => {
            setShowCreateDM(false);
            setSelectedChannel(channel);
            queryClient.invalidateQueries({ queryKey: ['messaging-channels'] });
          }}
        />
      )}

      {/* Add People to channel */}
      {showAddPeople && selectedChannel && (
        <AddPeopleModal
          channelId={selectedChannel.id}
          onClose={() => setShowAddPeople(false)}
          onAdded={() => {
            setShowAddPeople(false);
            queryClient.invalidateQueries({ queryKey: ['channel-detail', selectedChannel.id] });
            queryClient.invalidateQueries({ queryKey: ['messaging-channels'] });
          }}
        />
      )}
    </div>
    </MessageNavigationProvider>
  );
}

interface CreateChannelModalProps {
  onClose: () => void;
  onCreated: (channel: Channel) => void;
}

function CreateChannelModal({ onClose, onCreated }: CreateChannelModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [channelType, setChannelType] = useState<ChannelType>('public');
  const [error, setError] = useState('');

  const createMutation = useMutation({
    mutationFn: (request: CreateChannelRequest) =>
      messagingApi.createChannel(request),
    onSuccess: (channel) => onCreated(channel),
    onError: (err: any) => setError(err.message || 'Failed to create channel'),
  });

  const handleCreate = () => {
    if (!name.trim()) {
      setError('Channel name is required');
      return;
    }

    // Validate channel name format
    const validName = name
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    createMutation.mutate({
      name: validName,
      display_name: name,
      description,
      channel_type: channelType,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Create Channel</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded text-sm">
              {error}
            </div>
          )}

          <div>
            <Label htmlFor="name">Channel Name</Label>
            <div className="flex items-center gap-2 mt-1">
              <Hash className="h-4 w-4 text-muted-foreground" />
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. general, team-updates"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Names must be lowercase, with no spaces
            </p>
          </div>

          <div>
            <Label htmlFor="description">Description (optional)</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this channel about?"
              className="mt-1"
            />
          </div>

          <div>
            <Label>Channel Type</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button
                onClick={() => setChannelType('public')}
                className={cn(
                  'p-3 border rounded-lg text-left transition-colors',
                  channelType === 'public'
                    ? 'border-primary bg-primary/5'
                    : 'hover:border-slate-300'
                )}
              >
                <Hash className="h-5 w-5 mb-1" />
                <div className="font-medium text-sm">Public</div>
                <div className="text-xs text-muted-foreground">
                  Anyone can join
                </div>
              </button>
              <button
                onClick={() => setChannelType('private')}
                className={cn(
                  'p-3 border rounded-lg text-left transition-colors',
                  channelType === 'private'
                    ? 'border-primary bg-primary/5'
                    : 'hover:border-slate-300'
                )}
              >
                <Lock className="h-5 w-5 mb-1" />
                <div className="font-medium text-sm">Private</div>
                <div className="text-xs text-muted-foreground">
                  Invite only
                </div>
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!name.trim() || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Channel'}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface AddPeopleModalProps {
  channelId: number;
  onClose: () => void;
  onAdded: () => void;
}

// Add staff teammates and/or members (clients) to an existing channel.
function AddPeopleModal({ channelId, onClose, onAdded }: AddPeopleModalProps) {
  const [mode, setMode] = useState<'staff' | 'member'>('staff');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [error, setError] = useState('');
  const cboId = localStorage.getItem('active_cbo_id');

  // Staff only (no portal members, TRUS-434) but INCLUDING platform admins - see
  // listMessagingStaff.
  const { data: users = [], isLoading: loadingUsers } = useQuery({
    queryKey: ['staff-for-messaging', cboId],
    queryFn: () => listMessagingStaff(cboId),
    enabled: !!cboId && mode === 'staff',
  });
  const { data: memberResp, isLoading: loadingMembers } = useQuery({
    queryKey: ['members-for-dm', cboId, searchQuery],
    queryFn: () => membersAPI.listMembers({ search: searchQuery || undefined, page_size: 50 }),
    enabled: !!cboId && mode === 'member',
  });

  const items: { id: number; name: string; sub: string }[] =
    mode === 'staff'
      ? users
          .filter((u: OrganizationUser) =>
            searchQuery
              ? u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                u.email?.toLowerCase().includes(searchQuery.toLowerCase())
              : true
          )
          .map((u: OrganizationUser) => ({ id: u.id, name: u.name || 'Unknown', sub: u.email || '' }))
      : (memberResp?.members || []).map((m: any) => ({
          id: m.id,
          name: `${m.first_name || ''} ${m.last_name || ''}`.trim() || `Member #${m.id}`,
          sub: m.phone_no || m.phone_number || m.email || '',
        }));
  const loading = mode === 'staff' ? loadingUsers : loadingMembers;
  const toggleSelect = (id: number) =>
    setSelectedIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const addMutation = useMutation({
    mutationFn: () =>
      messagingApi.addMembers(
        channelId,
        mode === 'staff' ? selectedIds : undefined,
        mode === 'member' ? selectedIds : undefined,
      ),
    onSuccess: () => onAdded(),
    onError: (err: any) => setError(err.message || 'Failed to add people'),
  });

  const switchMode = (m: 'staff' | 'member') => {
    setMode(m);
    setSelectedIds([]);
    setSearchQuery('');
    setError('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Add people</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="p-6 space-y-4 flex-1 overflow-hidden flex flex-col">
          {error && (
            <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded text-sm">{error}</div>
          )}
          <div className="flex gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
            <button type="button" onClick={() => switchMode('staff')} className={cn('flex-1 rounded-md px-3 py-1.5 text-sm font-medium', mode === 'staff' ? 'bg-white dark:bg-slate-700 shadow-sm' : 'text-muted-foreground')}>Staff</button>
            <button type="button" onClick={() => switchMode('member')} className={cn('flex-1 rounded-md px-3 py-1.5 text-sm font-medium', mode === 'member' ? 'bg-white dark:bg-slate-700 shadow-sm' : 'text-muted-foreground')}>Members</button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={mode === 'member' ? 'Search members...' : 'Search by name or email...'} className="pl-9" />
          </div>
          <div className="flex-1 overflow-y-auto border rounded-lg">
            {loading ? (
              <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>
            ) : items.length === 0 ? (
              <div className="text-center text-muted-foreground py-8 text-sm">{mode === 'member' ? 'No members found' : 'No users found'}</div>
            ) : (
              <div className="divide-y">
                {items.map((item) => (
                  <button key={item.id} onClick={() => toggleSelect(item.id)} className={cn('w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800 text-left', selectedIds.includes(item.id) && 'bg-primary/10')}>
                    <div className={cn('w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium', mode === 'member' ? 'bg-amber-100 text-amber-700' : 'bg-primary/10')}>{item.name?.charAt(0).toUpperCase() || '?'}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{item.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{item.sub}</div>
                    </div>
                    {selectedIds.includes(item.id) && (
                      <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => (selectedIds.length ? addMutation.mutate() : setError('Select at least one person'))} disabled={selectedIds.length === 0 || addMutation.isPending}>
            {addMutation.isPending ? 'Adding...' : `Add${selectedIds.length ? ` (${selectedIds.length})` : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface CreateDMModalProps {
  onClose: () => void;
  onCreated: (channel: Channel) => void;
}

function CreateDMModal({ onClose, onCreated }: CreateDMModalProps) {
  // DM a staff teammate OR a member (client); pick MULTIPLE to start a group
  // conversation (created as a private channel).
  const [mode, setMode] = useState<'staff' | 'member'>('staff');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [error, setError] = useState('');

  // Get organization ID from localStorage
  const cboId = localStorage.getItem('active_cbo_id');

  // Staff to select from: staff only (no portal members, TRUS-434) but INCLUDING
  // platform admins - see listMessagingStaff.
  const { data: users = [], isLoading: loadingUsers } = useQuery({
    queryKey: ['staff-for-messaging', cboId],
    queryFn: () => listMessagingStaff(cboId),
    enabled: !!cboId && mode === 'staff',
  });

  // Fetch members to select from (server-side search)
  const { data: memberResp, isLoading: loadingMembers } = useQuery({
    queryKey: ['members-for-dm', cboId, searchQuery],
    queryFn: () => membersAPI.listMembers({ search: searchQuery || undefined, page_size: 50 }),
    enabled: !!cboId && mode === 'member',
  });

  // Unified pick list for the current mode.
  const items: { id: number; name: string; sub: string }[] =
    mode === 'staff'
      ? users
          .filter((u: OrganizationUser) =>
            searchQuery
              ? u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                u.email?.toLowerCase().includes(searchQuery.toLowerCase())
              : true
          )
          .map((u: OrganizationUser) => ({ id: u.id, name: u.name || 'Unknown', sub: u.email || '' }))
      : (memberResp?.members || []).map((m: any) => ({
          id: m.id,
          name: `${m.first_name || ''} ${m.last_name || ''}`.trim() || `Member #${m.id}`,
          sub: m.phone_no || m.phone_number || m.email || '',
        }));
  const loading = mode === 'staff' ? loadingUsers : loadingMembers;

  const createMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      // Single pick → 1:1 DM. Multiple → group conversation (private channel).
      if (ids.length === 1) {
        return mode === 'member' ? messagingApi.createDM(undefined, ids[0]) : messagingApi.createDM(ids[0]);
      }
      const names = items.filter((i) => ids.includes(i.id)).map((i) => i.name.split(' ')[0]);
      const label = names.slice(0, 3).join(', ') + (names.length > 3 ? `, +${names.length - 3}` : '');
      const channel = await messagingApi.createChannel({
        name: `group-${Date.now()}`,
        display_name: label || 'Group',
        description: '',
        channel_type: 'private',
      });
      await messagingApi.addMembers(
        channel.id,
        mode === 'staff' ? ids : undefined,
        mode === 'member' ? ids : undefined,
      );
      return channel;
    },
    onSuccess: (channel) => onCreated(channel),
    onError: (err: any) => setError(err.message || 'Failed to start conversation'),
  });

  const switchMode = (m: 'staff' | 'member') => {
    setMode(m);
    setSelectedIds([]);
    setSearchQuery('');
    setError('');
  };

  const toggleSelect = (id: number) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleCreate = () => {
    if (selectedIds.length === 0) {
      setError(`Please select at least one ${mode === 'member' ? 'member' : 'user'}`);
      return;
    }
    createMutation.mutate(selectedIds);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">New Direct Message</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6 space-y-4 flex-1 overflow-hidden flex flex-col">
          {error && (
            <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded text-sm">
              {error}
            </div>
          )}

          {/* Staff vs Member toggle */}
          <div className="flex gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
            <button
              type="button"
              onClick={() => switchMode('staff')}
              className={cn('flex-1 rounded-md px-3 py-1.5 text-sm font-medium', mode === 'staff' ? 'bg-white dark:bg-slate-700 shadow-sm' : 'text-muted-foreground')}
            >
              Staff teammate
            </button>
            <button
              type="button"
              onClick={() => switchMode('member')}
              className={cn('flex-1 rounded-md px-3 py-1.5 text-sm font-medium', mode === 'member' ? 'bg-white dark:bg-slate-700 shadow-sm' : 'text-muted-foreground')}
            >
              Member
            </button>
          </div>

          <div>
            <Label htmlFor="search">{mode === 'member' ? 'Find a member' : 'Find a user'}</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={mode === 'member' ? 'Search members by name...' : 'Search by name or email...'}
                className="pl-9"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto border rounded-lg">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : items.length === 0 ? (
              <div className="text-center text-muted-foreground py-8 text-sm">
                {mode === 'member' ? 'No members found' : 'No users found'}
              </div>
            ) : (
              <div className="divide-y">
                {items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => toggleSelect(item.id)}
                    className={cn(
                      'w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left',
                      selectedIds.includes(item.id) && 'bg-primary/10'
                    )}
                  >
                    <div className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium',
                      mode === 'member' ? 'bg-amber-100 text-amber-700' : 'bg-primary/10'
                    )}>
                      {item.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{item.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{item.sub}</div>
                    </div>
                    {selectedIds.includes(item.id) && (
                      <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={selectedIds.length === 0 || createMutation.isPending}
          >
            {createMutation.isPending
              ? 'Starting...'
              : selectedIds.length > 1
                ? `Create group (${selectedIds.length})`
                : 'Start Conversation'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default MessagingWorkspace;
